// filepath: src/pages/DetectionsPage.jsx
// alertupdates Tier-6 — Detections & Alerts: a unified per-camera timeline of
// detections + gate outcomes + alert sent/suppressed/filtered reasons, filterable
// by camera / date / time window, with CSV export. Reads parvekshak_api.
import { useState, useEffect, useCallback } from 'react';
import '../css/diagnostics.css';
import { apiFetch, getToken, STREAM_API } from '../utils/streamApi';
import { MissedAlertsTab } from './AdminConfigPage';   // moved out of Admin Config → shown here

function todayStr() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
function downloadFile(name, text, mime = 'text/plain') {
  // Android WebView can't hand blob: URLs to the native download path
  // (net::ERR_FILE_NOT_FOUND) — hand the bytes to the app shell instead.
  if (window.FlutterDownload) {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    window.FlutterDownload.postMessage(JSON.stringify({ filename: name, mime, data: btoa(binary) }));
    return;
  }
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function csvCell(v) { return `"${String(v ?? '').replace(/"/g, '""')}"`; }
async function getJson(path) {
  const res = await apiFetch(path);
  try { return await res.json(); } catch { return null; }
}

const REASON_LABEL = {
  cooldown: 'cooldown', outside_window: 'outside alert window', known_seen: 'known person',
  scene_wide_motion: 'scene-wide motion (whole-frame)', oscillation: 'oscillating — no net direction',
  animal: 'animal', exclusion_zone: 'ignore zone',
};

// Event-type filter chips. A plain detection row carries no `type`, so it is
// normalised to 'detection' — otherwise "All" and "Detections" would differ.
const TYPE_CHIPS = [
  { v: '',                 label: 'All' },
  { v: 'alert_sent',       label: '🚨 Alerts' },
  { v: 'alert_suppressed', label: '🔇 Suppressed' },
  { v: 'filtered',         label: '🍃 Filtered' },
  { v: 'gate',             label: '🚧 Gate' },
  { v: 'detection',        label: 'Detections' },
];
const evType = d => d.type || 'detection';
const RULE_LABEL = {
  RULE_BLOCK_NO_FACE_NO_SKELETON:   'no face, no skeleton',
  RULE_BLOCK_SKELETON_NO_PATH:      'skeleton, no net movement',
  RULE_BLOCK_SKELETON_UNVERIFIED:   'skeleton + path, conditions unverified',
  RULE_BLOCK_FACE_LARGE_UNVERIFIED: 'large face, clothing/CLIP not confirmed',
  RULE_BLOCK_FACE_LARGE_RTM_REJECT: 'large face, skeleton rejected (RTMPose)',
  RULE_PASS_FACE_LARGE:  'large face + clothing + CLIP',
  RULE_PASS_FACE_SMALL:  'small face + path + clothing + CLIP',
  RULE_PASS_SKELETON:    'skeleton + path + directional',
};
function evLabel(d) {
  if (d.type === 'alert_sent')       return '🚨 ALERT SENT';
  if (d.type === 'alert_suppressed') return '🔇 suppressed';
  if (d.type === 'filtered')         return '🍃 filtered';
  if (d.type === 'gate')             return d.outcome === 'PASS' ? '✅ gate PASS' : '🚧 gate BLOCK';
  return 'detection';
}
function evDetail(d) {
  if (d.type === 'alert_sent')
    return `${d.label || ''}${d.alert_id ? ' · ' + d.alert_id : ''}`;
  if (d.type === 'alert_suppressed' || d.type === 'filtered') {
    const base = REASON_LABEL[d.reason] || d.reason || '';
    let why = base;
    if (d.reason === 'cooldown' && d.remaining_s != null) why += ` (${d.remaining_s}s left)`;
    if (d.dropped != null) why += ` (${d.dropped} dropped)`;
    if (d.label && d.label !== base) why += ` · ${d.label}`;   // append only if it adds info
    return why;
  }
  if (d.type === 'gate') {
    const r = RULE_LABEL[d.rule] || d.rule || '';
    return `${r} · skeleton=${d.skeleton} clothing=${d.clothing} clip=${d.clip} path=${d.track_path_px}px`;
  }
  const confs = Array.isArray(d.confs) ? d.confs.join(', ') : '';
  return `${d.yolo_boxes ?? ''} box(es) conf=[${confs}] → ${d.kept ?? ''} kept`;
}
function evClass(d) {
  if (d.type === 'alert_sent')       return 'diag-ev-alert';
  if (d.type === 'alert_suppressed') return 'diag-ev-supp';
  if (d.type === 'filtered')         return 'diag-ev-filtered';
  if (d.type === 'gate')             return d.outcome === 'PASS' ? 'diag-ev-pass' : 'diag-ev-block';
  return '';
}

// Deep logging — the switch that makes a camera's SILENCE readable.
//
// Normally the timeline records only what a camera found. A frame where the model
// found boxes and every filter dropped them writes nothing, so "the person was
// rejected for being 62px tall against an 80px floor" looks identical to "nobody was
// there". With this on, each camera also writes a health row every 30s (how many
// detection cycles it got, how close its motion gate came to firing) and a row naming
// the filter whenever every box is dropped.
//
// Summaries, not per-frame prints — a few hundred extra rows a day — so it is safe to
// leave on for a night of testing, which Debug Mode is not. Takes effect on save.
function DeepLoggingToggle() {
  const [on,     setOn]     = useState(null);   // null = not loaded yet
  const [frames, setFrames] = useState(true);   // context frame — the costly half
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState('');

  useEffect(() => {
    let alive = true;
    apiFetch('/settings/advanced-config')
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        setOn(!!d.detection_diag_logs);
        setFrames(d.detection_proof_frames !== false);
      })
      .catch(() => { if (alive) setErr('load failed'); });
    return () => { alive = false; };
  }, []);

  // Send ONLY the field being changed — every key in the model is Optional and is
  // left untouched when omitted, so this cannot clobber another setting.
  const save = async (patch, apply) => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const res = await apiFetch('/settings/advanced-config', { method: 'PUT', json: patch });
      if (!res.ok) throw new Error(String(res.status));
      apply();
    } catch {
      setErr('save failed');
    } finally {
      setBusy(false);
    }
  };

  if (on === null) return null;
  return (
    <>
      <button className="diag-btn"
              onClick={() => save({ detection_diag_logs: !on }, () => setOn(!on))}
              disabled={busy}
              title={on
                ? 'Deep logging is ON — each camera also records why it kept nothing. Turn off when testing is done.'
                : 'Record why a camera kept nothing: per-camera health every 30s, plus the filter that dropped every box. Safe to leave on overnight.'}
              style={{ opacity: busy ? 0.55 : 1, borderColor: on ? '#16a34a' : undefined }}>
        {on ? '🔎 Deep logging: ON' : '🔎 Deep logging: off'}
        {err && <span style={{ color: '#dc2626', marginLeft: 6 }}>· {err}</span>}
      </button>

      {/* The rows above are a few hundred log lines a day and cost nothing. The
          context frame is the only diagnostic with a CONTINUOUS cost — a downscale
          every 5s per camera, roughly 1.5% of one core across seven — so it gets its
          own control rather than forcing a choice between evidence and throughput.
          Shown only while deep logging is on, since it does nothing otherwise. */}
      {on && (
        <label className="diag-btn"
               title="On each blocked record, also store one frame from a few seconds
 earlier plus the face crop if the visit captured one — so a verdict is not labelled
 from a single possibly-stale picture. Costs about 1.5% of one CPU core across seven
 cameras. Untick if the box is short of CPU."
               style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                        cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.55 : 1 }}>
          <input type="checkbox"
                 checked={frames}
                 disabled={busy}
                 onChange={() => save({ detection_proof_frames: !frames },
                                      () => setFrames(!frames))} />
          Extra evidence <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(~1.5% CPU)</span>
        </label>
      )}
    </>
  );
}

export default function DetectionsPage() {
  const [cams, setCams]           = useState([]);
  const [dets, setDets]           = useState([]);
  const [camFilter, setCamFilter] = useState('');
  const [date, setDate]           = useState(todayStr);
  const [fromT, setFromT]         = useState('');
  const [toT, setToT]             = useState('');
  const [err, setErr]             = useState('');
  const [view, setView]           = useState('timeline');   // 'timeline' | 'missed'
  const [typeF, setTypeF]         = useState('');           // '' = all event types
  const [reasonF, setReasonF]     = useState('');           // '' = all reasons
  const [tok, setTok]             = useState('');           // for <img> proof thumbnails

  // <img> can't send an Authorization header, so alert images go through ?token=.
  useEffect(() => { getToken().then(t => setTok(t || '')).catch(() => {}); }, []);
  const alertImgUrl = (id) =>
    (tok && id) ? `${STREAM_API}/alerts/${encodeURIComponent(id + '.jpg')}/image?token=${tok}` : null;
  // Non-alert rows (gate BLOCK, ignore-zone suppression) carry their own small
  // thumbnail, written per event rather than reused from an alert.
  const proofUrl = (f) =>
    (tok && f) ? `${STREAM_API}/logs/detections/proof/${encodeURIComponent(f)}?token=${tok}` : null;

  // Client-side filtering: the rows are already fetched for the chosen camera /
  // date / time window, so type+reason narrow what's shown without a refetch.
  const matchesFilter = useCallback(
    d => (!typeF || evType(d) === typeF) && (!reasonF || (d.reason || '') === reasonF),
    [typeF, reasonF]);

  const loadCams = useCallback(async () => {
    try {
      const d = await getJson('/health/cameras');
      setCams((d?.cameras || []).map(c => c.name));
    } catch { /* ignore */ }
  }, []);

  const loadDets = useCallback(async () => {
    setErr('');
    try {
      const q = new URLSearchParams({ limit: '500' });
      if (date)      q.set('date', date.replace(/-/g, ''));
      if (camFilter) q.set('cam', camFilter);
      if (fromT)     q.set('start', fromT);
      if (toT)       q.set('end', toT);
      const d = await getJson(`/logs/detections?${q.toString()}`);
      setDets(d?.events || []);
      if (d?.error) setErr(d.error);
    } catch (e) { setErr(e.message || 'Failed to load'); }
  }, [camFilter, date, fromT, toT]);

  const downloadDets = useCallback(async () => {
    try {
      const q = new URLSearchParams({ limit: '5000' });
      if (date)      q.set('date', date.replace(/-/g, ''));
      if (camFilter) q.set('cam', camFilter);
      if (fromT)     q.set('start', fromT);
      if (toT)       q.set('end', toT);
      const d = await getJson(`/logs/detections?${q.toString()}`);
      const rows = [['time', 'camera', 'type', 'outcome', 'rule', 'yolo_boxes', 'confidences',
                     'kept', 'reason', 'label', 'alert_id', 'skeleton', 'clothing', 'clip', 'track_path_px']];
      (d?.events || []).filter(matchesFilter).forEach(e => rows.push([
        e.ts ? new Date(e.ts * 1000).toISOString() : '',
        e.cam, e.type || 'detection', e.outcome || '', e.rule || '', e.yolo_boxes,
        Array.isArray(e.confs) ? e.confs.join(' ') : '',
        e.kept, e.reason || '', e.label || '', e.alert_id || '',
        e.skeleton ?? '', e.clothing ?? '', e.clip ?? '', e.track_path_px ?? '',
      ]));
      const csv = rows.map(r => r.map(csvCell).join(',')).join('\n');
      downloadFile(`detections_${[date, camFilter].filter(Boolean).join('_')}.csv`, csv, 'text/csv');
    } catch (e) { setErr(e.message || 'Download failed'); }
  }, [date, camFilter, fromT, toT, matchesFilter]);

  useEffect(() => { loadCams(); }, [loadCams]);
  useEffect(() => { loadDets(); }, [loadDets]);

  const shown          = dets.filter(matchesFilter);
  const reasonsPresent = [...new Set(dets.map(d => d.reason).filter(Boolean))].sort();

  return (
    <div className="diag-page">
      <div className="diag-card-head" style={{ marginBottom: 12 }}>
        <h1 className="diag-title" style={{ margin: 0 }}>Detection Logs</h1>
        <div className="diag-filters">
          <button className="diag-btn" style={{ opacity: view === 'timeline' ? 1 : 0.55 }}
                  onClick={() => setView('timeline')}>Timeline</button>
          <button className="diag-btn" style={{ opacity: view === 'missed' ? 1 : 0.55 }}
                  onClick={() => setView('missed')}>⚠ Missed Alerts</button>
          <DeepLoggingToggle />
        </div>
      </div>
      {err && <div className="diag-error">{err}</div>}

      {view === 'missed' && (
        <section className="diag-card"><MissedAlertsTab /></section>
      )}

      {view === 'timeline' && (
      <section className="diag-card">
        <div className="diag-card-head">
          <h2>Timeline <span className="diag-sub">(detected / gate / alert sent / suppressed / filtered)</span></h2>
          <div className="diag-filters">
            <select value={camFilter} onChange={e => setCamFilter(e.target.value)}>
              <option value="">All cameras</option>
              {cams.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <input type="date" className="diag-date" value={date} max={todayStr()}
                   onChange={e => setDate(e.target.value || todayStr())} />
            <input type="time" className="diag-date" value={fromT} title="From time"
                   onChange={e => setFromT(e.target.value)} />
            <input type="time" className="diag-date" value={toT} title="To time"
                   onChange={e => setToT(e.target.value)} />
            <button className="diag-btn" onClick={loadDets}>&#8635;</button>
            <button className="diag-btn" onClick={downloadDets}>⬇ CSV</button>
          </div>
        </div>

        <div className="diag-filters" style={{ flexWrap: 'wrap', gap: 6, margin: '4px 0 10px' }}>
          {TYPE_CHIPS.map(c => (
            <button key={c.v} className="diag-btn"
                    style={{ opacity: typeF === c.v ? 1 : 0.5 }}
                    onClick={() => setTypeF(c.v)}>{c.label}</button>
          ))}
          <select value={reasonF} onChange={e => setReasonF(e.target.value)} title="Filter by reason">
            <option value="">All reasons</option>
            {reasonsPresent.map(r => (
              <option key={r} value={r}>{REASON_LABEL[r] || r}</option>
            ))}
          </select>
          <span style={{ fontSize: 12, opacity: 0.7, alignSelf: 'center' }}>
            {shown.length} of {dets.length} events
          </span>
        </div>
        <div className="diag-table-wrap">
          <table className="diag-table">
            <thead>
              <tr><th>Time</th><th>Camera</th><th>Event</th><th>Detail</th><th>Proof</th></tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={5} className="diag-empty">
                  {dets.length ? 'No events match this filter.' : 'No events.'}
                </td></tr>
              )}
              {shown.slice().reverse().map((d, i) => {
                const img = d.type === 'alert_sent' ? alertImgUrl(d.alert_id) : proofUrl(d.proof);
                return (
                  <tr key={i} className={evClass(d)}>
                    <td>{d.ts ? new Date(d.ts * 1000).toLocaleTimeString() : '—'}</td>
                    <td>{d.cam}</td>
                    <td>{evLabel(d)}</td>
                    <td>{evDetail(d)}</td>
                    <td>
                      {img
                        ? <a href={img} target="_blank" rel="noreferrer" title="Open full image">
                            <img src={img} alt="" loading="lazy"
                                 style={{ height: 34, borderRadius: 4, display: 'block' }} />
                          </a>
                        : <span style={{ opacity: 0.35 }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      )}
    </div>
  );
}
